---
title: "Advanced Risk: Drawdown, Expectancy, Kelly (and Why Not Full Kelly)"
slug: drawdown-expectancy-kelly
shortTitle: "Drawdown, Expectancy, Kelly"
description: "The math behind long-term risk: how drawdowns compound, how to measure a strategy's expectancy, what the Kelly formula says, and why most traders risk far less than it suggests."
level: advanced
category: trading
course: advanced
order: 7
topics: [risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/drawdown-expectancy-kelly.svg
faqs:
  - q: "What is a drawdown?"
    a: "A drawdown is how far your account value has fallen from its last peak, in USDT or as a percentage. A fall from 5,000 to 4,000 USDT is a 20% drawdown, and it needs a 25% gain to get back to the peak."
  - q: "What does positive expectancy mean?"
    a: "It means that, on the numbers you measured, the average trade made money after the losers were taken into account. It is an estimate from past trades, not a promise about future ones, and it can change as markets change."
  - q: "Does the Kelly criterion tell me how much leverage to use?"
    a: "No. Kelly suggests a share of your account to risk on a trade, meaning what you would lose if the stop is hit. Leverage only changes the margin a position ties up. Most traders treat Kelly as an upper limit and risk much less."
  - q: "Why is full Kelly considered too aggressive?"
    a: "It relies on knowing your true win rate and payoff exactly, which no trader does. Small overestimates lead to over-betting, and even with perfect numbers full Kelly produces very deep drawdowns along the way."
  - q: "What is fractional Kelly?"
    a: "Fractional Kelly means risking a set share of the Kelly figure, such as a half or a quarter. It gives up some theoretical growth in exchange for much smaller swings and more room for error in your estimates."
---

Most risk lessons stop at one trade: where the stop goes and how big the position is. This one zooms out to hundreds of trades. Three ideas decide whether an account survives that long: **drawdown**, the fall from a peak; **expectancy**, what an average trade is worth; and the **Kelly criterion**, a formula that suggests how much of your account to risk per trade.

Kelly is famous, and often misused. It is built to maximize long-run growth under ideal conditions, not to keep you safe, and the gap between those two goals is why most traders who know the formula risk far less than it says. Every figure below is an illustrative example, not a target.

<CTA />

<KeyTakeaways>

- **Losses compound against you.** A 50% drawdown needs a 100% gain to recover, so deep drawdowns are much harder to climb out of than they look.
- **Expectancy is the edge.** Win rate × average win minus loss rate × average loss tells you what the average trade has been worth. If it is not positive after costs, no sizing rule can help.
- **Kelly sizes for growth, not safety.** Its answer depends on estimates you never know exactly, and even when they are right it brings brutal drawdowns.
- **Fractional and fixed-fraction risk are the practical answer.** Risking a small, fixed share of equity, well below Kelly, leaves room for errors, losing streaks and changing markets.

</KeyTakeaways>

## Drawdown: why losses are harder to undo

A **drawdown** is the fall in your account value from its highest point, in USDT or as a percentage. Because each gain and loss works on a changed balance, the gain needed to recover is always bigger than the loss: **gain needed = drawdown ÷ (1 − drawdown)**.

<CompareTable caption="Drawdown and the gain needed to recover">

| Drawdown | Gain needed to get back |
| --- | --- |
| 10% | 11.1% |
| 20% | 25% |
| 30% | 42.9% |
| 50% | 100% |
| 75% | 300% |

</CompareTable>

Losing streaks are where drawdowns come from, and they are more common than intuition suggests. With a 45% win rate, the chance that any given run of five trades is all losses is 0.55 multiplied by itself five times, about **5%**. Over a few hundred trades, streaks of seven or eight losses are entirely normal.

<Example title="Eight losses in a row (illustrative)">

Each loss takes a fixed share of what is left, so after eight straight losses:

- Risking 1% per trade: 0.99 to the power of 8 = 0.923, a **7.7%** drawdown.
- Risking 2%: 0.98 to the power of 8 = 0.851, a **14.9%** drawdown.
- Risking 5%: 0.95 to the power of 8 = 0.663, a **33.7%** drawdown, which needs a 51% gain to recover.

</Example>

On {{BRAND}}, the **Portfolio** page's Overview shows your maximum drawdown over a chosen period, and in **Account → Alerts** you can add an **Equity drawdown** alert that fires when equity falls a set percentage below its high for the day (UTC).

{/* TODO: verify with platform — the max. drawdown row (Trader Risk panel) and the win ratio, Average profit and Average loss rows (Trader Profitability panel) on Portfolio → Overview. */}

## Expectancy: what an average trade is worth

**Expectancy** is the average result per trade over many trades:

**Expectancy = (win rate × average win) − (loss rate × average loss)**

It is often expressed in **R**, where 1R is the amount you risk on a trade. Measuring in R lets you compare results across different position sizes.

<Example title="Expectancy from 50 trades (illustrative)">

Over 50 closed trades you had 20 winners averaging +90 USDT and 30 losers averaging −40 USDT, after costs.

- Win rate = 20 ÷ 50 = 40%, loss rate = 60%.
- Expectancy = (0.40 × 90) − (0.60 × 40) = 36 − 24 = **+12 USDT per trade**.
- In R, with 1R = 40 USDT: the average win is 90 ÷ 40 = 2.25R, so expectancy = (0.40 × 2.25) − (0.60 × 1) = 0.90 − 0.60 = **+0.3R**.

</Example>

Use net figures. The **Net PnL** column in **Closed Positions** already includes fees and swap, and the Portfolio Overview lists your win ratio, average profit and average loss for a period. Fifty trades is a small sample, though: a couple of lucky winners can make a weak strategy look strong. [Risk/reward and win rate](/academy/article/risk-reward-and-win-rate) explains how the two numbers trade off.

## The Kelly criterion

The Kelly formula takes your win rate (W) and your payoff ratio (R, the average win divided by the average loss) and returns the share of equity to risk per trade:

**Kelly % = W − (1 − W) ÷ R**

With the numbers above, W = 0.40 and R = 2.25, so Kelly = 0.40 − 0.60 ÷ 2.25 = 0.40 − 0.267 = **13.3%**. On a 5,000 USDT account, that means risking about 667 USDT per trade: the amount lost if the stop is hit, not the position value and not the margin.

Under idealized conditions (fixed, known odds and independent trades), risking exactly this share maximizes the long-run growth rate of the account. That is all it promises. It says nothing about how bumpy the ride is, and it assumes your inputs are exactly right.

## Why traders do not use full Kelly

- **Your inputs are estimates.** Suppose your true win rate is 35%, not 40%. True Kelly is then 0.35 − 0.65 ÷ 2.25 = **6.1%**. Risking 13.3% is more than twice that, and above twice Kelly the account tends to shrink over time even though each trade still has positive expectancy (0.35 × 2.25 − 0.65 = +0.14R).
- **The drawdowns are severe.** Eight straight losses at 13.3% each leave 0.867 to the power of 8 = 0.318 of the account: a **68%** drawdown. In the idealized math, a full-Kelly bettor has roughly a 50% chance of seeing the account halve at some point.
- **Trading is not a coin toss.** Stops can slip in gaps, related trades lose together, edges fade as markets change, and leveraged positions are closed automatically at {{STOP_OUT_LEVEL}}.
- **People are not machines.** Few traders keep following their rules through a 50% drawdown.

<CompareTable caption="The same strategy at different risk levels (5,000 USDT account, illustrative)">

| Approach | Risk per trade | In USDT | After 8 straight losses |
| --- | --- | --- | --- |
| Full Kelly | 13.3% | 667 | −68.2% |
| Half Kelly | 6.7% | 333 | −42.4% |
| Quarter Kelly | 3.3% | 167 | −23.8% |
| Fixed 1% | 1% | 50 | −7.7% |

</CompareTable>

**Fractional Kelly** risks a set share of the Kelly figure. In this example, half Kelly keeps about three-quarters of the theoretical growth rate while cutting the swings sharply, and in the same idealized math its chance of ever halving the account falls to about one in eight. Many traders go further and use **fixed-fraction risk**, such as 0.5% to 2% of equity per trade, and use Kelly only as a ceiling and a check: if your honest numbers give a Kelly figure at or below zero, there is no edge to size, and no position-sizing rule will fix that.

<Warning title="No formula makes leveraged trading safe">

Kelly, expectancy and drawdown math describe averages over many trades; your next trade can still lose, and a stop can fill beyond its level in a fast market. Leverage magnifies losses, and most traders who trade CFDs lose money. Use these tools to risk less, not to justify risking more.

</Warning>

## Putting it to work on {{BRAND}}

<Steps>
<Step title="Collect honest numbers">

Note at least 50 to 100 trades from **Closed Positions**, using **Net PnL**. Demo results are a start; live results are better.

</Step>
<Step title="Work out expectancy and Kelly">

Calculate win rate, average win, average loss, expectancy and the Kelly figure. Use cautious numbers: round the win rate down and the average loss up.

</Step>
<Step title="Pick a fixed fraction well below Kelly">

Choose a risk per trade, for example 1% of equity, and turn it into USDT. In the order panel, switch on **TP / SL** and check that the **Loss (USDT)** figure at your stop matches it.

</Step>
<Step title="Write drawdown rules">

Decide in advance what happens at, say, a 10% drawdown: halve your risk, pause, or review. Back it up with an **Equity drawdown** alert.

</Step>
</Steps>

<ProTip title="Recalculate, and use the worse number">

Recompute your expectancy every month or every 50 trades. When estimates disagree, size from the worse one. Overestimating your edge costs far more than underestimating it.

</ProTip>

## Conclusion

Drawdowns compound, so the size of each loss matters more than it seems. Expectancy tells you whether there is an edge worth sizing at all. Kelly shows the theoretical maximum-growth bet, and its sensitivity to bad estimates and its deep drawdowns are exactly why practical traders use a fraction of it or a small fixed percentage instead. Measure honestly, risk a small share per trade, and connect your sizing to the [position-sizing](/academy/article/position-sizing) habits you already use.
